// Scenario 5: Not Booked - Region Preference
// Shows AI recommendation within user's preferred region
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
// Removed unused firebase callable imports
import { islandScopedDocumentFilter, sanityFetch } from '../../lib/sanity';
import { useRegionsData } from '../../hooks/useRegionsData';
import RegionsMapLite from '../maps/RegionsMapLite';
import AreaProfileHero from './AreaProfileHero';
import AreaProfileSidebar from './AreaProfileSidebar';
import NearbyAreas from './NearbyAreas';
import HomebaseActionPanel from './HomebaseActionPanel';
import {
  getHomebaseAreaSlug,
  getHomebaseIslandKey,
  isHomebaseItineraryReady,
  MULTI_ISLAND_PLANNER_COMING_SOON_PATH,
} from '../../utils/homebaseGeography';

const RegionPrefScenario = ({ data, interviewId, planningState = {} }) => {
  const navigate = useNavigate();
  const [displayedArea, setDisplayedArea] = useState(null); // Currently shown area
  const [areaData, setAreaData] = useState(null); // Full area data from Sanity
  const [isShowingRecommendation, setIsShowingRecommendation] = useState(true); // vs user selection

  // Get master areas data for validation
  const { areasData: allAreas } = useRegionsData();
  
  // Extract data from backend response
  const islandKey = getHomebaseIslandKey(data);
  const itineraryReady = isHomebaseItineraryReady(islandKey);
  const recommendedArea = data?.summary?.areaRecommendation?.area;
  const region = data?.preferredRegion;
  const summary = data?.summary?.areaRecommendation;

  const { ready, loading, error, retry } = planningState;
  const ctaPlanningState = useMemo(() => ({
    ready,
    loading,
    error,
    retry
  }), [ready, loading, error, retry]);

  // Initialize displayed area with recommendation
  useEffect(() => {
    if (recommendedArea && !displayedArea) {
      setDisplayedArea(recommendedArea);
    }
  }, [recommendedArea, displayedArea]);

  // Fetch area data from Sanity when displayed area changes
  useEffect(() => {
    const fetchAreaData = async () => {
      if (!displayedArea) return;
      
      try {
        const explicitSlug = displayedArea === data?.area ? data?.areaSlug : null;
        const areaSlug = getHomebaseAreaSlug(displayedArea, islandKey, explicitSlug);
        
        const query = `*[_type == "area" && ${islandScopedDocumentFilter()} && slug.current == $slug][0]{
          _id,
          title,
          oneLiner,
          heroImage{asset->{url}, alt},
          slug,
          fit,
          tradeoffs,
          walkability,
          beachProfile,
          diningDensity,
          logisticsNotes,
          signatureExperiences[]->{
            _id,
            title,
            slug,
            heroImage{asset->{url}, alt},
            oneLiner
          },
          region->{
            _id,
            title,
            slug
          }
        }`;
        
        const result = await sanityFetch(query, { slug: areaSlug });
        
        if (result) {
          setAreaData(result);
        } else {
          // Fallback to basic data
          setAreaData({
            title: displayedArea,
            oneLiner: `Beautiful ${displayedArea} offers stunning scenery and local charm in ${region}.`,
            heroImage: { asset: { url: '/api/placeholder/800/400' }, alt: `${displayedArea} landscape` }
          });
        }
      } catch (error) {
        console.error('[RegionPrefScenario] Error fetching area data:', error);
        // Fallback to basic data
        setAreaData({
          title: displayedArea,
          oneLiner: `Beautiful ${displayedArea} offers stunning scenery and local charm in ${region}.`,
          heroImage: { asset: { url: '/api/placeholder/800/400' }, alt: `${displayedArea} landscape` }
        });
      }
    };

    fetchAreaData();
  }, [displayedArea, region, islandKey, data?.area, data?.areaSlug]);

  // Early return check after hooks
  if (!data || !recommendedArea) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-white/70">No homebase data available for this scenario.</p>
      </div>
    );
  }

  const handleEditInterview = () => {
    navigate(`/interview/review/${interviewId}`);
  };

  const handleGenerateItinerary = () => {
    if (!itineraryReady) {
      navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH);
      return;
    }

    // Prefer current selection; fall back to recommended area
    const areaToConfirm = displayedArea || recommendedArea;
    const regionToConfirm = region;

    if (!areaToConfirm || !regionToConfirm) {
      console.warn('[RegionPrefScenario] Missing area or region on generate click:', {
        displayedArea,
        recommendedArea,
        region
      });
    }

    // Navigate to preview and let the preview page perform homebaseConfirm first
    const q = new URLSearchParams({
      confirmArea: areaToConfirm || '',
      confirmRegion: regionToConfirm || '',
      scenario: 'not-booked-region-pref'
    }).toString();
    navigate(`/itinerary/${interviewId}?${q}`);
  };

  // Normalize area names for comparison (handles Hawaiian diacritics)
  const normalizeAreaName = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .replace(/[ʻ'`]/g, '')  // Remove ʻokinas and apostrophes
      .replace(/[āáàâä]/g, 'a')
      .replace(/[ēéèêë]/g, 'e')
      .replace(/[īíìîï]/g, 'i')
      .replace(/[ōóòôö]/g, 'o')
      .replace(/[ūúùûü]/g, 'u')
      .trim();
  };

  const handleAreaChange = (areaName) => {
    setDisplayedArea(areaName);
    
    // Use normalized comparison to handle Hawaiian diacritics
    const isRecommendedArea = normalizeAreaName(areaName) === normalizeAreaName(recommendedArea);
    setIsShowingRecommendation(isRecommendedArea);
  };

  const handleMapAreaClick = (type, areaName) => {
    if (type === 'area' && allAreas) {
      // Validate area against master list
      const area = allAreas.find(a => 
        a.title === areaName ||
        a.title.toLowerCase() === areaName.toLowerCase() ||
        a.slug.current === areaName ||
        a.slug.current === areaName.toLowerCase()
      );
      
      if (area && area.region?.title === region) {
        // Only allow selection within the preferred region
        handleAreaChange(area.title);
      }
    }
  };

  // Header message logic
  const getHeaderMessage = () => {
    // Use proper area name from Sanity data if available, otherwise use displayedArea
    const properAreaName = areaData?.title || displayedArea;
    
    if (isShowingRecommendation) {
      return `We recommend ${properAreaName} on the ${region}.`;
    } else {
      return `You've selected ${properAreaName} in ${region}.`;
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 pb-8">
        
        {/* Primary CTAs */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4 mb-4">
          <button 
            onClick={handleEditInterview}
            className="btn-secondary w-full md:w-72 px-8 py-4 text-lg"
          >
            Edit Interview
          </button>
          <div className="w-full md:w-72">
            <HomebaseActionPanel
              planningState={ctaPlanningState}
              onGenerate={handleGenerateItinerary}
              itineraryReady={itineraryReady}
              onUnavailable={() => navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH)}
            />
          </div>
        </div>

        {/* Header Message */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2 text-white">
            {getHeaderMessage()}
          </h2>
        </div>

        {/* LLM Recommendation - Only show if displaying recommendation */}
        {isShowingRecommendation && data?.summary && (
          <div className="glass-card rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4 text-white">
              Why {region} is Perfect for Your Trip
            </h3>
            
            {/* Strategic Overview */}
            {data.summary.lodgingInsights && (
              <div className="mb-6">
                <p className="text-white/90 leading-relaxed">
                  {data.summary.lodgingInsights}
                </p>
              </div>
            )}

            {/* Area-Specific Reasons */}
            {summary?.explanation && summary.explanation.length > 0 && (
              <div>
                <h4 className="font-semibold text-white mb-3">Why {areaData?.title || displayedArea} Specifically:</h4>
                <ul className="space-y-3">
                  {summary.explanation.map((reason, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-400 mr-3 mt-1">•</span>
                      <span className="text-white/90">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Area Profile */}
        {areaData ? (
          <div className="mb-8">
            {/* Hero Section */}
            <AreaProfileHero areaData={areaData} />

            {/* Overview Section with Map */}
            <div className="grid md:grid-cols-2 gap-12 mb-8">
              {/* Left Sidebar - Area Details */}
              <AreaProfileSidebar areaData={areaData} />

              {/* Interactive Map */}
              <div>
                <div className="h-[400px] rounded-lg overflow-hidden border border-gray-600">
                  {allAreas ? (
                    <RegionsMapLite
                      islandKey={islandKey}
                      selectable={true}
                      areasSelectable={true}
                      selectedRegion={region}
                      selectedArea={displayedArea}
                      onSelect={handleMapAreaClick}
                      focusedAreaMode={true}
                      className="w-full h-full"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                      <p>Loading map...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-8 flex items-center justify-center h-48">
            <p className="text-white/70">Loading area profile...</p>
          </div>
        )}

        {/* Primary CTAs */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4 mb-8">
          <button 
            onClick={handleEditInterview}
            className="btn-secondary w-full md:w-72 px-8 py-4 text-lg"
          >
            Edit Interview
          </button>
          <div className="w-full md:w-72">
            <HomebaseActionPanel
              planningState={ctaPlanningState}
              onGenerate={handleGenerateItinerary}
              itineraryReady={itineraryReady}
              onUnavailable={() => navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH)}
            />
          </div>
        </div>

        {/* Nearby Areas */}
        <NearbyAreas 
          currentAreaTitle={areaData?.title}
          regionTitle={region}
          regionSlug={areaData?.region?.slug?.current}
          onAreaSelect={handleAreaChange}
        />

        {/* Bottom CTAs */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4">
          <button 
            onClick={handleEditInterview}
            className="btn-secondary w-full md:w-72 px-8 py-4 text-lg"
          >
            Edit Interview
          </button>
          <div className="w-full md:w-72">
            <HomebaseActionPanel
              planningState={ctaPlanningState}
              onGenerate={handleGenerateItinerary}
              itineraryReady={itineraryReady}
              onUnavailable={() => navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegionPrefScenario;
